import { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Trash2, Edit3, Save, X, Plus } from 'lucide-react';
import Switch from '../../../../common/Switch';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;
// const backendUrl = 'http://127.0.0.1:8000';

// ─── Keyframes ────────────────────────────────────────────────────────────────

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Layout ────────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  width: 100%;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

// ─── Header ────────────────────────────────────────────────────────────────────

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03);
  border: 1px solid #e8ecf1;
  padding: 18px 24px;
  position: relative;
  z-index: 1;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const TitleText = styled.div``;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px;
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

// ─── Language Toggle ────────────────────────────────────────────────────────────

const LangToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 10px;
  flex-shrink: 0;
`;

const LangLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${({ active }) => (active ? '#2563eb' : '#94a3b8')};
  transition: color 0.2s;
  user-select: none;
  letter-spacing: 0.3px;
`;

// ─── Add Button ───────────────────────────────────────────────────────────────

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ disabled }) => (disabled ? '#9ca3af' : '#2563eb')};
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
`;

// ─── Divider ──────────────────────────────────────────────────────────────────

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin-bottom: 16px;
`;

// ─── Error / State ────────────────────────────────────────────────────────────

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #dc2626;
  font-weight: 500;
`;

const CenterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 10px;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
`;

const StateText = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  font-weight: 500;
`;

const RefreshBar = styled.div`
  text-align: center;
  padding: 8px;
  font-size: 12px;
  color: #2563eb;
  font-weight: 600;
`;

// ─── Accordion ─────────────────────────────────────────────────────────────────

const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #e2e8f0;
  &:last-child { border-bottom: none; }
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ open }) => (open ? '#eff6ff' : '#ffffff')};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${({ open }) => (open ? '#eff6ff' : '#f8fafc')};
  }
`;

const AccordionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const IndexDot = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ open }) => (open ? '#2563eb' : '#e2e8f0')};
  color: ${({ open }) => (open ? '#ffffff' : '#64748b')};
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
`;

const HeadingText = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ open }) => (open ? '#2563eb' : '#1e293b')};
  transition: color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  padding: 6px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $color, disabled }) => (disabled ? '#9ca3af' : $color || '#64748b')};
  display: flex;
  align-items: center;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: ${({ $hoverBg }) => $hoverBg || '#f1f5f9'};
  }
`;

const ChevronIcon = styled.span`
  font-size: 11px;
  color: ${({ open }) => (open ? '#2563eb' : '#94a3b8')};
  transition: transform 0.2s, color 0.15s;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  flex-shrink: 0;
`;

const AccordionBody = styled.div`
  max-height: ${({ open }) => (open ? '1200px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const AccordionContent = styled.div`
  padding: 14px 16px;
  background: #fafafa;
  font-size: 14px;
  color: #374151;
  line-height: 1.75;
  border-top: 1px solid #e2e8f0;

  p { margin: 0 0 8px; }
  p:last-child { margin-bottom: 0; }
  ul, ol { padding-left: 20px; margin: 8px 0; }
  li { margin-bottom: 4px; }
  strong { color: #1e293b; }
`;

const BodyInner = styled.div`
  background: #fafafa;
  border-top: 1px solid #e2e8f0;
  padding: 16px;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

const ColLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 8px 0;
`;

const HeadingInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  outline: none;
  margin-bottom: 12px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const SaveBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ disabled }) => (disabled ? '#9ca3af' : '#16a34a')};
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #15803d;
  }
`;

const CancelBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ disabled }) => (disabled ? '#e5e7eb' : '#f1f5f9')};
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #e2e8f0;
  }
`;

// ─── EditablePointsList ───────────────────────────────────────────────────────

const PointRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 6px;
`;

const Bullet = styled.span`
  color: #9ca3af;
  flex-shrink: 0;
`;

const PointInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }
`;

const DeletePointBtn = styled.button`
  padding: 5px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #ef4444;
  display: flex;
  align-items: center;
  transition: background 0.15s;

  &:hover {
    background: #fef2f2;
  }
`;

const AddPointBtn = styled.button`
  font-size: 14px;
  color: #2563eb;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 500;
  padding: 0;
  transition: color 0.15s;

  &:hover {
    color: #1d4ed8;
  }
`;

const EditablePointsList = ({ language, points, onPointChange, onAddPoint, onDeletePoint }) => (
  <div style={{ marginBottom: '8px' }}>
    {points.map((point, pointIndex) => (
      <PointRow key={`${language}-${pointIndex}`}>
        <Bullet>•</Bullet>
        <PointInput
          type="text"
          value={point}
          onChange={(e) => onPointChange(language, pointIndex, e.target.value)}
          placeholder={`Point ${pointIndex + 1}`}
        />
        <DeletePointBtn onClick={() => onDeletePoint(language, pointIndex)}>
          <Trash2 size={14} />
        </DeletePointBtn>
      </PointRow>
    ))}
    <AddPointBtn onClick={() => onAddPoint(language)}>+ Add Point</AddPointBtn>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Floodadvisory = () => {
  const [advisories, setAdvisories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTetum, setShowTetum] = useState(false);
  const [editingAdvisoryId, setEditingAdvisoryId] = useState(null);
  const [editableEnglishHeading, setEditableEnglishHeading] = useState('');
  const [editableEnglishPoints, setEditableEnglishPoints] = useState([]);
  const [editableTetumHeading, setEditableTetumHeading] = useState('');
  const [editableTetumPoints, setEditableTetumPoints] = useState([]);
  const [openIndex, setOpenIndex] = useState(0);

  const fetchAdvisories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/flood/flood-advisories/`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}. Response: ${errorText.substring(0, 200)}`);
      }
      const data = await response.json();
      setAdvisories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch advisories:', e);
      setError('Failed to load advisories. Please try again later.');
      setAdvisories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdvisories();
  }, [fetchAdvisories]);

  const parseDescriptionToList = (htmlString) => {
    if (!htmlString) return [];
    const matches = [...htmlString.matchAll(/<li>(.*?)<\/li>/g)];
    return matches.map((match) => match[1]);
  };

  const listToDescriptionHtml = (itemsArray) => {
    if (!itemsArray || itemsArray.length === 0) return '<ul></ul>';
    return `<ul>${itemsArray.map((item) => `<li>${item}</li>`).join('')}</ul>`;
  };

  const handleEdit = (advisory, index) => {
    setEditingAdvisoryId(advisory.id);
    setEditableEnglishHeading(advisory.englishheading || '');
    setEditableEnglishPoints(parseDescriptionToList(advisory.englishdescription || ''));
    setEditableTetumHeading(advisory.tetumheading || '');
    setEditableTetumPoints(parseDescriptionToList(advisory.tetumdescription || ''));
    setOpenIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingAdvisoryId(null);
  };

  const handleSaveEdit = async () => {
    if (editingAdvisoryId === null) return;

    const updatedEnglishDescriptionHtml = listToDescriptionHtml(editableEnglishPoints);
    const updatedTetumDescriptionHtml = listToDescriptionHtml(editableTetumPoints);

    const payload = {
      englishheading: editableEnglishHeading,
      englishdescription: updatedEnglishDescriptionHtml,
      tetumheading: editableTetumHeading,
      tetumdescription: updatedTetumDescriptionHtml,
    };

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/flood/flood-advisories/${editingAdvisoryId}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorDataText = 'Error details not available in JSON format.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to save advisory (ID: ${editingAdvisoryId}): ${response.status} - ${errorDataText}`);
      }
      handleCancelEdit();
      fetchAdvisories();
    } catch (e) {
      console.error('Error saving advisory:', e);
      setError(`Failed to save changes for advisory ID ${editingAdvisoryId}: ${e.message}`);
      setIsLoading(false);
    }
  };

  const handlePointChange = (language, index, value) => {
    if (language === 'english') {
      const newPoints = [...editableEnglishPoints];
      newPoints[index] = value;
      setEditableEnglishPoints(newPoints);
    } else if (language === 'tetum') {
      const newPoints = [...editableTetumPoints];
      newPoints[index] = value;
      setEditableTetumPoints(newPoints);
    }
  };

  const handleAddPointToList = (language) => {
    if (language === 'english') {
      setEditableEnglishPoints([...editableEnglishPoints, '']);
    } else if (language === 'tetum') {
      setEditableTetumPoints([...editableTetumPoints, '']);
    }
  };

  const handleDeletePoint = (language, index) => {
    if (language === 'english') {
      setEditableEnglishPoints(editableEnglishPoints.filter((_, i) => i !== index));
    } else if (language === 'tetum') {
      setEditableTetumPoints(editableTetumPoints.filter((_, i) => i !== index));
    }
  };

  const handleAddNewAdvisory = async () => {
    const newAdvisoryData = {
      englishheading: 'Flood Advisory',
      englishdescription: '<ul><li>Point 1</li></ul>',
      tetumheading: 'Avizu Inundasaun',
      tetumdescription: '<ul><li>Foun 1</li></ul>',
    };

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/flood/flood-advisories/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAdvisoryData),
      });
      if (!response.ok) {
        let errorDataText = 'Error details not available in JSON format.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to add new advisory: ${response.status} - ${errorDataText}`);
      }
      fetchAdvisories();
    } catch (e) {
      console.error('Error adding new advisory:', e);
      setError(`Failed to add new advisory: ${e.message}`);
      setIsLoading(false);
    }
  };

  const handleDeleteAdvisory = async (advisoryId) => {
    if (!window.confirm('Are you sure you want to delete this advisory section? This action cannot be undone.')) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/flood/flood-advisories/${advisoryId}/`, {
        method: 'DELETE',
        headers: {},
      });
      if (!response.ok && response.status !== 204) {
        let errorDataText = 'Error details not available.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to delete advisory (ID: ${advisoryId}): ${response.status} - ${errorDataText}`);
      }
      fetchAdvisories();
    } catch (e) {
      console.error('Error deleting advisory:', e);
      setError(`Failed to delete advisory (ID: ${advisoryId}): ${e.message}`);
      setIsLoading(false);
    }
  };

  const toggleOpen = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  if (isLoading && advisories.length === 0 && !error) {
    return (
      <PageWrapper>
        <CenterBox>
          <Spinner />
          <StateText>Loading advisories...</StateText>
        </CenterBox>
      </PageWrapper>
    );
  }

  if (error && advisories.length === 0) {
    return (
      <PageWrapper>
        <CenterBox>
          <span style={{ fontSize: 32 }}>⚠️</span>
          <StateText style={{ color: '#dc2626' }}>{error}</StateText>
        </CenterBox>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeaderRow>
        <TitleBlock>
          <IconBadge>
            <WaterDropIcon sx={{ fontSize: 28, color: '#2563eb' }} />
          </IconBadge>
          <TitleText>
            <Title>
              {showTetum
                ? 'Informasaun No Konsellu Kona-ba Inundasaun'
                : 'Information and Safety Advice Regarding Floods'}
            </Title>
            <Subtitle>
              {showTetum
                ? 'Avizu no orientasaun ba situasaun inundasaun'
                : 'Advisories and guidance for flood situations'}
            </Subtitle>
          </TitleText>
        </TitleBlock>

        <HeaderRight>
          <LangToggleWrapper>
            <LangLabel active={!showTetum}>🇬🇧EN</LangLabel>
            <Switch onChange={() => setShowTetum(!showTetum)} />
            <LangLabel active={showTetum}>🇹🇱TT</LangLabel>
          </LangToggleWrapper>
          <AddButton
            onClick={handleAddNewAdvisory}
            disabled={isLoading || editingAdvisoryId !== null}
          >
            <Plus size={14} />
            Add Section
          </AddButton>
        </HeaderRight>
      </HeaderRow>

      <Divider />

      {error && advisories.length > 0 && (
        <ErrorBanner>⚠️ {error}</ErrorBanner>
      )}

      {advisories.length > 0 ? (
        <AccordionList>
          {advisories.map((advisory, index) => {
            const isOpen = openIndex === index;
            const isEditing = editingAdvisoryId === advisory.id;
            const actionsDisabled = isLoading || editingAdvisoryId !== null;
            const heading = showTetum
              ? advisory.tetumheading || 'Laiha tipu'
              : advisory.englishheading || 'No heading available';
            const description = showTetum
              ? advisory.tetumdescription || '<p>Laiha deskrisaun.</p>'
              : advisory.englishdescription || '<p>No description available.</p>';

            return (
              <AccordionItem key={advisory.id}>
                <AccordionHeader
                  open={isOpen}
                  onClick={() => toggleOpen(index)}
                  type="button"
                >
                  <AccordionLeft>
                    <IndexDot open={isOpen}>{index + 1}</IndexDot>
                    <HeadingText open={isOpen}>{heading}</HeadingText>
                  </AccordionLeft>

                  <HeaderActions onClick={(e) => e.stopPropagation()}>
                    <IconBtn
                      type="button"
                      title="Edit"
                      disabled={actionsDisabled}
                      $color="#2563eb"
                      $hoverBg="#eff6ff"
                      onClick={() => handleEdit(advisory, index)}
                    >
                      <Edit3 size={15} />
                    </IconBtn>
                    <IconBtn
                      type="button"
                      title="Delete"
                      disabled={actionsDisabled}
                      $color="#ef4444"
                      $hoverBg="#fef2f2"
                      onClick={() => handleDeleteAdvisory(advisory.id)}
                    >
                      <Trash2 size={15} />
                    </IconBtn>
                  </HeaderActions>

                  <ChevronIcon open={isOpen}>▼</ChevronIcon>
                </AccordionHeader>

                <AccordionBody open={isOpen}>
                  {isEditing ? (
                    <BodyInner>
                      <TwoCol>
                        <div>
                          <ColLabel>English</ColLabel>
                          <HeadingInput
                            type="text"
                            value={editableEnglishHeading}
                            onChange={(e) => setEditableEnglishHeading(e.target.value)}
                            placeholder="English heading"
                          />
                          <EditablePointsList
                            language="english"
                            points={editableEnglishPoints}
                            onPointChange={handlePointChange}
                            onAddPoint={handleAddPointToList}
                            onDeletePoint={handleDeletePoint}
                          />
                        </div>
                        <div>
                          <ColLabel>Tetum</ColLabel>
                          <HeadingInput
                            type="text"
                            value={editableTetumHeading}
                            onChange={(e) => setEditableTetumHeading(e.target.value)}
                            placeholder="Tetum heading"
                          />
                          <EditablePointsList
                            language="tetum"
                            points={editableTetumPoints}
                            onPointChange={handlePointChange}
                            onAddPoint={handleAddPointToList}
                            onDeletePoint={handleDeletePoint}
                          />
                        </div>
                      </TwoCol>
                      <EditActions>
                        <CancelBtn
                          type="button"
                          disabled={isLoading}
                          onClick={handleCancelEdit}
                        >
                          <X size={14} />
                          Cancel
                        </CancelBtn>
                        <SaveBtn
                          type="button"
                          disabled={isLoading}
                          onClick={handleSaveEdit}
                        >
                          <Save size={14} />
                          Save
                        </SaveBtn>
                      </EditActions>
                    </BodyInner>
                  ) : (
                    <AccordionContent
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}
                </AccordionBody>
              </AccordionItem>
            );
          })}
        </AccordionList>
      ) : (
        <CenterBox>
          <span style={{ fontSize: 36 }}>💧</span>
          <StateText>
            {showTetum
              ? 'Laiha avizu kona-ba inundasaun.'
              : 'No flood advisories are currently available.'}
          </StateText>
          <AddButton onClick={handleAddNewAdvisory} disabled={isLoading}>
            <Plus size={14} />
            Add Section
          </AddButton>
        </CenterBox>
      )}

      {isLoading && advisories.length > 0 && (
        <RefreshBar>⟳ Refreshing data...</RefreshBar>
      )}
    </PageWrapper>
  );
};

export default Floodadvisory;
